import { useState } from 'react';
import { Folder, FolderOpen, ChevronRight, ChevronDown, Plus, Lock, Eye, Download as DownloadIcon, MoreVertical } from 'lucide-react';

// Client-access presets shown as a small lock/eye/download indicator on each folder.
export const CLIENT_ACCESS_META = {
  none: { label: 'Internal only', Icon: Lock, color: 'text-[#C4727F]' },
  view: { label: 'Client: view', Icon: Eye, color: 'text-[#C67D5B]' },
  download: { label: 'Client: view + download', Icon: DownloadIcon, color: 'text-[#7DA68A]' },
};

function buildTree(folders) {
  const byParent = {};
  for (const f of folders) {
    const p = f.parent_id || '__root__';
    (byParent[p] = byParent[p] || []).push(f);
  }
  for (const k in byParent) byParent[k].sort((a, b) => (a.order || 0) - (b.order || 0));
  return byParent;
}

export default function DrawingFolderTree({ folders, counts, selectedId, onSelect, onAddSub, onManage, onDropDrawing, canManage = true }) {
  const [expanded, setExpanded] = useState({});
  const [dragOverId, setDragOverId] = useState(null);
  const toggle = (id) => setExpanded(e => ({ ...e, [id]: !(e[id] ?? true) }));

  const byParent = buildTree(folders);
  const isExpanded = (id, depth) => expanded[id] ?? (depth < 1);

  // Flatten the visible tree into ordered rows in plain JS (no self-referencing
  // JSX component — that trips the visual-edits babel plugin's traversal).
  const rows = [];
  const walk = (parentKey, depth) => {
    for (const f of (byParent[parentKey] || [])) {
      const hasChildren = (byParent[f.id] || []).length > 0;
      const open = isExpanded(f.id, depth);
      rows.push({ f, depth, hasChildren, open });
      if (hasChildren && open) walk(f.id, depth + 1);
    }
  };
  walk('__root__', 0);

  const totalAll = Object.values(counts).reduce((s, n) => s + (n || 0), 0);

  return (
    <div className="space-y-0.5" data-testid="drawing-folder-tree">
      {/* All pseudo-folder */}
      <div onClick={() => onSelect('__all__')}
        className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg cursor-pointer text-sm ${selectedId === '__all__' ? 'bg-[#3A8C8C]/12 text-[#3A8C8C]' : 'text-[#2C2C2E] hover:bg-[#E8E6E1]/60'}`}
        data-testid="folder-node-all">
        <span className="w-[13px]" /><Folder size={15} className="text-[#6B7280]" />
        <span className="flex-1">All Drawings</span>
        <span className="text-[10px] text-[#6B7280]">{totalAll}</span>
      </div>

      {rows.map(({ f, depth, hasChildren, open }) => {
        const isSel = selectedId === f.id;
        const access = CLIENT_ACCESS_META[f.client_access] || CLIENT_ACCESS_META.view;
        const AccessIcon = access.Icon;
        const isDragOver = dragOverId === f.id;
        return (
          <div key={f.id}
            onClick={() => onSelect(f.id)}
            onDragOver={e => { e.preventDefault(); setDragOverId(f.id); }}
            onDragLeave={() => setDragOverId(null)}
            onDrop={e => { e.preventDefault(); setDragOverId(null); const id = e.dataTransfer.getData('drawing-id'); if (id) onDropDrawing(id, f.id); }}
            style={{ paddingLeft: `${8 + depth * 14}px` }}
            className={`group flex items-center gap-1.5 pr-2 py-1.5 rounded-lg cursor-pointer text-sm transition-colors ${isSel ? 'bg-[#3A8C8C]/12 text-[#3A8C8C]' : isDragOver ? 'bg-[#3A8C8C]/8' : 'text-[#2C2C2E] hover:bg-[#E8E6E1]/60'}`}
            data-testid={`folder-node-${f.id}`}
          >
            {hasChildren ? (
              <button onClick={e => { e.stopPropagation(); toggle(f.id); }} className="shrink-0 text-[#6B7280]">
                {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
              </button>
            ) : <span className="w-[13px] shrink-0" />}
            {isSel ? <FolderOpen size={15} className="shrink-0 text-[#3A8C8C]" /> : <Folder size={15} className="shrink-0 text-[#6B7280]" />}
            <span className="flex-1 truncate">{f.name}</span>
            <AccessIcon size={11} className={`shrink-0 ${access.color}`} title={access.label} />
            {counts[f.id] > 0 && <span className="text-[10px] text-[#6B7280] shrink-0">{counts[f.id]}</span>}
            {canManage && (
              <>
                <button onClick={e => { e.stopPropagation(); onAddSub(f.id); }} title="Add subfolder"
                  className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-[#3A8C8C]/10 text-[#3A8C8C] shrink-0"><Plus size={12} /></button>
                <button onClick={e => { e.stopPropagation(); onManage(f); }} title="Folder settings"
                  className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-[#E8E6E1] text-[#6B7280] shrink-0"><MoreVertical size={12} /></button>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
